import { Link } from "react-router-dom";
import { Card, PageHeader } from "../components/ui";
import { trackEvent } from "../lib/analytics";
import { useShellSession } from "../hooks/useShellSession";

interface MoreCard {
  title: string;
  subtitle: string;
  to: string;
  icon: string;
  dietitianOnly?: boolean;
  userOnly?: boolean;
}

const cards: MoreCard[] = [
  { title: "Dietitian says", subtitle: "Read dietitian-reviewed nutrition editorials", to: "/app/dietitian-says", icon: "🩺" },
  { title: "Visits", subtitle: "Request, track, and manage appointments", to: "/app/visits", icon: "🗓️" },
  { title: "AI insights", subtitle: "View pantry analyses and statuses", to: "/app/ai-insights", icon: "✨", userOnly: true },
  { title: "Scans", subtitle: "Upload and manage pantry scans", to: "/app/scans", icon: "📷", userOnly: true },
  { title: "Pantry", subtitle: "Browse and maintain inventory", to: "/app/pantry", icon: "🥫", userOnly: true },
  { title: "Shopping list", subtitle: "Plan your next grocery run, pull from pantry", to: "/app/shopping-list", icon: "🛒", userOnly: true },
  { title: "Explore", subtitle: "Search nutrition data", to: "/app/explore", icon: "🔎" },
  {
    title: "Admin user costs",
    subtitle: "Daily/range and lifetime costs (requires user-cost-visibility group)",
    to: "/app/admin/user-costs",
    icon: "💸",
  },
  { title: "Settings", subtitle: "Account and profile", to: "/app/settings", icon: "⚙️" },
  { title: "Author posts", subtitle: "Draft and publish dietitian blogs", to: "/app/dietitian-says/mine", icon: "✍️", dietitianOnly: true },
  { title: "AI review console", subtitle: "Review and edit member AI insights", to: "/app/ai-insights/review", icon: "🧠", dietitianOnly: true },
];

export function MorePage() {
  const { session } = useShellSession();
  const isDietitian = Boolean(session.user?.isDietitian);
  const visibleCards = cards.filter((card) => {
    if (card.dietitianOnly && !isDietitian) {
      return false;
    }
    if (card.userOnly && isDietitian) {
      return false;
    }
    return true;
  });

  return (
    <div data-testid="more-page" className="space-y-4 pb-4">
      <PageHeader title="More" subtitle="Additional PantryRx workflows and controls." />

      <div className="grid gap-3 sm:grid-cols-2">
        {visibleCards.map((card, index) => (
          <Link key={card.title} to={card.to} className="group" onClick={() => trackEvent("more_nav_clicked", { destination: card.to, title: card.title })}>
            <Card
              flat
              className="relative overflow-hidden transition hover:translate-y-[-2px] hover:shadow-ambient"
            >
              <div className="pointer-events-none absolute right-[-1.9rem] top-[-1.9rem] h-24 w-24 rounded-full bg-[color-mix(in_srgb,var(--pr-accent)_12%,transparent)]" />
              <div className="relative flex items-center gap-4">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[color-mix(in_srgb,var(--pr-accent)_14%,transparent)] text-xl">
                  {card.icon}
                </span>
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold">{card.title}</h3>
                  <p className="text-sm text-[var(--pr-text-muted)]">{card.subtitle}</p>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <Card className="space-y-2">
        <h3 className="text-base font-semibold">Role capabilities</h3>
        <p className="text-sm text-[var(--pr-text-muted)]">
          Current role: <span className="font-semibold uppercase tracking-wide">{session.user?.role || "member"}</span>
        </p>
        <p className="text-sm text-[var(--pr-text-muted)]">
          {isDietitian
            ? "Dietitian mode is active: blog authoring, visit triage, and cross-user AI insight review are enabled."
            : "Switch to a dietitian profile to unlock authoring, appointment triage, and AI insight review tools."}
        </p>
      </Card>
    </div>
  );
}
